/**
 * Behavior for sections/header-immersive.liquid, ported 1:1 from the approved static demo
 * (assets/global/header-scroll-opaque.js + assets/global/global-ui.js in the source review site).
 *
 * Three independent state sources, matching the source demo exactly:
 * - scroll position -> .is-scrolled-opaque (forces solid background past a scroll threshold)
 * - content underneath the header -> .header-dark-ink (keeps light ink only while the header
 *   sits over a section marked data-header-ink="light"; dark ink everywhere else)
 * - hover / focus-within / menu-open -> handled entirely in CSS, no JS involved
 *
 * Plus one addition beyond the demo: reserving body space (`global-header-offset`, an
 * already-present-but-previously-unused class in global-ui.css) on templates whose
 * `enable_overlay_*` setting is off — see sections/header-immersive.liquid.
 *
 * Plus a second addition: publishing this header's real height as the theme-standard
 * `--header-height` custom property on `document.body`. layout/theme.liquid only does this for
 * the stock `<header-component>` (its own height-sync script bails out early when that element
 * is missing, which it always is on templates using this immersive header), so anything that
 * reads `--header-height` — e.g. snippets/product-information-content.liquid's sticky product
 * details offset — saw an unset variable and defaulted to 0, sticking right under the viewport
 * top instead of under this fixed header. `--header-h` itself can't be read from outside
 * `.site-header` (it's scoped locally and changes across breakpoints, see the file comment near
 * that variable), so this measures the rendered height directly instead of trying to mirror it.
 */
(function () {
  function init(header) {
    if (header.dataset.immersiveReady === 'true') return;
    header.dataset.immersiveReady = 'true';

    initOverlayOffset(header);
    initScrollOpacity(header);
    initMenu(header);
    initInkDetection(header);
    initHeaderHeightOffset(header);
  }

  function initOverlayOffset(header) {
    if (header.dataset.overlay === 'false') {
      document.body.classList.add('global-header-offset');
    }
  }

  function initHeaderHeightOffset(header) {
    function update() {
      var height = header.getBoundingClientRect().height || header.offsetHeight || 0;
      document.body.style.setProperty('--header-height', height + 'px');
    }

    window.addEventListener('resize', update);
    window.addEventListener('load', update, { once: true });
    update();
  }

  function initScrollOpacity(header) {
    // Horizon's own base.css hands real scrolling off to `.page-wrapper` at >=990px
    // (html/body get `overflow: hidden`), so `window.scrollY` alone stays 0 on desktop —
    // the demo this was ported from has no such wrapper, so it never hit this.
    var pageWrapper = document.querySelector('.page-wrapper');

    function update() {
      var headerHeight = header.getBoundingClientRect().height || header.offsetHeight || 0;
      var threshold = Math.max(0, window.innerHeight * 0.75 - headerHeight);
      var scrollTop = Math.max(window.scrollY, pageWrapper ? pageWrapper.scrollTop : 0);
      header.classList.toggle('is-scrolled-opaque', scrollTop >= threshold);
    }

    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    window.addEventListener('pageshow', update);
    window.addEventListener('load', update, { once: true });
    if (pageWrapper) pageWrapper.addEventListener('scroll', update, { passive: true });
    update();
  }

  function initMenu(header) {
    var menuToggle = header.querySelector('.menu-toggle');
    var mobileLinks = header.querySelectorAll('.mobile-nav a');
    if (!menuToggle) return;

    function setMenu(open) {
      header.classList.toggle('menu-open', open);
      document.body.classList.toggle('menu-open', open);
      menuToggle.setAttribute('aria-expanded', String(open));
    }

    menuToggle.addEventListener('click', function () {
      setMenu(!header.classList.contains('menu-open'));
    });

    mobileLinks.forEach(function (link) {
      link.addEventListener('click', function () {
        setMenu(false);
      });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') setMenu(false);
    });
  }

  function initInkDetection(header) {
    var toneZones = document.querySelectorAll('[data-header-ink]');
    if (!toneZones.length) return;

    function updateHeaderTone() {
      var stack = document.elementsFromPoint(window.innerWidth / 2, Math.max(1, header.offsetHeight / 2));
      var zone = null;

      for (var index = 0; index < stack.length; index += 1) {
        if (header.contains(stack[index])) continue;
        zone = stack[index].closest('[data-header-ink]');
        if (zone) break;
      }

      header.classList.toggle('header-dark-ink', !zone || zone.dataset.headerInk !== 'light');
    }

    updateHeaderTone();
    window.addEventListener('scroll', updateHeaderTone, { passive: true });
    window.addEventListener('resize', updateHeaderTone);
    window.addEventListener('load', updateHeaderTone, { once: true });
    var pageWrapper = document.querySelector('.page-wrapper');
    if (pageWrapper) pageWrapper.addEventListener('scroll', updateHeaderTone, { passive: true });
  }

  function initAll() {
    document.querySelectorAll('.site-header').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor re-renders the section in place (no full page reload) on setting changes.
  document.addEventListener('shopify:section:load', function (event) {
    var header = event.target.querySelector && event.target.querySelector('.site-header');
    if (header) init(header);
  });
})();
